import { Component, OnInit, OnDestroy } from '@angular/core'
import { RedefineService, RedefineProperty, RedefineImportResponse, RedefineSector } from 'src/app/services/redefine.service'
import { MerchantService } from 'src/app/services/merchant.service'
import { EnterpriseContextService } from 'src/app/services/enterprise-context.service'
import { Merchant } from '@parkupp/core'
import { Subscription } from 'rxjs'
import Swal from 'sweetalert2'
import { ENTERPRISE_CONFIGS } from 'src/app/config/enterprise.config'

@Component({
    selector: 'app-admin-redefine-importer',
    templateUrl: './admin-redefine-importer.component.html',
    styleUrls: ['./admin-redefine-importer.component.scss'],
})
export class AdminRedefineImporterComponent implements OnInit, OnDestroy {
    private merchantsSubscription: Subscription | null = null
    private enterpriseSubscription: Subscription | null = null

    // State
    isLoadingPortfolios = true
    isLoadingSectors = false
    isLoadingProperties = false
    isImporting = false
    isSyncingCoordinates = false

    // Data
    private allPortfolios: Merchant[] = []
    portfolios: Merchant[] = []
    selectedPortfolioKey: string = ''
    
    // Sectors
    sectors: RedefineSector[] = []
    selectedSectorIds: Set<number> = new Set()
    
    // Properties
    properties: RedefineProperty[] = []
    selectedPropertyIds: Set<number> = new Set()

    // Results
    lastImportResult: RedefineImportResponse | null = null

    constructor(
        private redefineService: RedefineService,
        private merchantService: MerchantService,
        public enterpriseContext: EnterpriseContextService
    ) {}

    ngOnInit(): void {
        this.loadPortfolios()
        this.loadSectors()
        
        // Subscribe to enterprise context changes to re-filter portfolios
        this.enterpriseSubscription = this.enterpriseContext.currentEnterprise$.subscribe(() => {
            this.filterPortfoliosByEnterprise()
        })
    }

    ngOnDestroy(): void {
        if (this.merchantsSubscription) {
            this.merchantsSubscription.unsubscribe()
        }
        if (this.enterpriseSubscription) {
            this.enterpriseSubscription.unsubscribe()
        }
    }

    /**
     * Load all portfolios, then filter by enterprise
     */
    async loadPortfolios(): Promise<void> {
        this.isLoadingPortfolios = true
        console.log('[RedefineImporter] Loading all portfolios...')
        try {
            this.merchantsSubscription = this.merchantService.listAll().subscribe({
                next: (merchants) => {
                    console.log('[RedefineImporter] Loaded merchants:', merchants.length)
                    this.allPortfolios = merchants.sort((a, b) => 
                        (a.companyName || '').localeCompare(b.companyName || '')
                    )
                    this.filterPortfoliosByEnterprise()
                    this.isLoadingPortfolios = false
                },
                error: (error) => {
                    console.error('Error loading portfolios:', error)
                    this.isLoadingPortfolios = false
                    Swal.fire({
                        title: 'Error',
                        text: 'Failed to load portfolios',
                        icon: 'error',
                    })
                },
            })
        } catch (error) {
            console.error('Error loading portfolios:', error)
            this.isLoadingPortfolios = false
        }
    }
    
    /**
     * Load sectors from Redefine API
     */
    async loadSectors(): Promise<void> {
        this.isLoadingSectors = true
        try {
            this.sectors = await this.redefineService.getSectors()
            console.log('[RedefineImporter] Loaded sectors:', this.sectors.length)
            
            // Pre-select Retail and Office sectors by default (skip Industrial)
            this.sectors.forEach(sector => {
                const sectorNameLower = sector.SectorName.toLowerCase()
                if (sectorNameLower.includes('retail') || sectorNameLower.includes('office')) {
                    this.selectedSectorIds.add(sector.SectorId)
                }
            })
            console.log('[RedefineImporter] Pre-selected sectors:', Array.from(this.selectedSectorIds))
        } catch (error: any) {
            console.error('Error loading sectors:', error)
            // Don't show error - sectors are optional
        } finally {
            this.isLoadingSectors = false
        }
    }

    /**
     * Toggle sector selection
     */
    toggleSectorSelection(sectorId: number): void {
        if (this.selectedSectorIds.has(sectorId)) {
            this.selectedSectorIds.delete(sectorId)
        } else {
            this.selectedSectorIds.add(sectorId)
        }
    }

    /**
     * Check if a sector is selected
     */
    isSectorSelected(sectorId: number): boolean {
        return this.selectedSectorIds.has(sectorId)
    }

    /**
     * Filter portfolios based on current enterprise selection
     * - ParkUpp (default) or no selection: show all portfolios
     * - Other enterprise (e.g., Redefine): show only that enterprise's portfolios
     */
    filterPortfoliosByEnterprise(): void {
        const enterpriseKey = this.enterpriseContext.currentEnterpriseKey
        const isDefaultEnterprise = !enterpriseKey || ENTERPRISE_CONFIGS[enterpriseKey]?.isDefault
        
        if (isDefaultEnterprise) {
            // Show all portfolios when ParkUpp (default) is selected
            this.portfolios = [...this.allPortfolios]
        } else {
            // Filter to only show portfolios belonging to the selected enterprise
            this.portfolios = this.allPortfolios.filter(
                (m) => m.denormEnterpriseKey === enterpriseKey
            )
        }
        
        console.log('[RedefineImporter] Filtered portfolios:', this.portfolios.length, 'for enterprise:', enterpriseKey || 'all')
        
        // Clear selection if the selected portfolio is no longer in the filtered list
        if (this.selectedPortfolioKey && !this.portfolios.find(p => p.$key === this.selectedPortfolioKey)) {
            this.selectedPortfolioKey = ''
        }
    }

    /**
     * Handle portfolio selection change
     */
    onPortfolioChange(): void {}

    /**
     * Fetch properties from Redefine API, filtered by selected sectors
     */
    async fetchProperties(): Promise<void> {
        if (this.isLoadingProperties) return

        this.isLoadingProperties = true
        this.properties = []
        this.selectedPropertyIds.clear()
        this.lastImportResult = null

        try {
            // Pass selected sector IDs to filter the results
            const sectorIds = this.selectedSectorIds.size > 0 
                ? Array.from(this.selectedSectorIds) 
                : undefined
            
            console.log('[RedefineImporter] Fetching properties with sector filter:', sectorIds)
            this.properties = await this.redefineService.getProperties(sectorIds)
            console.log('[RedefineImporter] Fetched properties:', this.properties?.length || 0)
        } catch (error: any) {
            console.error('Error fetching properties:', error)
            await Swal.fire({
                title: 'Error',
                text: error.message || 'Failed to fetch properties from Redefine API',
                icon: 'error',
            })
        } finally {
            this.isLoadingProperties = false
        }
    }

    /**
     * Toggle selection of a property
     */
    togglePropertySelection(propertyId: number): void {
        if (this.selectedPropertyIds.has(propertyId)) {
            this.selectedPropertyIds.delete(propertyId)
        } else {
            this.selectedPropertyIds.add(propertyId)
        }
    }

    /**
     * Check if a property is selected
     */
    isPropertySelected(propertyId: number): boolean {
        return this.selectedPropertyIds.has(propertyId)
    }

    /**
     * Select all properties
     */
    selectAll(): void {
        this.properties.forEach((p) => this.selectedPropertyIds.add(p.PropertyId))
    }

    /**
     * Deselect all properties
     */
    deselectAll(): void {
        this.selectedPropertyIds.clear()
    }

    /**
     * Get count of selected properties
     */
    get selectedCount(): number {
        return this.selectedPropertyIds.size
    }

    /**
     * Import selected properties
     */
    async importSelected(): Promise<void> {
        if (this.isImporting) return
        if (!this.selectedPortfolioKey) {
            await Swal.fire({
                title: 'Select Portfolio',
                text: 'Please select a portfolio to import properties into',
                icon: 'warning',
            })
            return
        }
        if (this.selectedPropertyIds.size === 0) {
            await Swal.fire({
                title: 'Select Properties',
                text: 'Please select at least one property to import',
                icon: 'warning',
            })
            return
        }

        // Find portfolio name
        const selectedPortfolio = this.portfolios.find((p) => p.$key === this.selectedPortfolioKey)
        const portfolioName = selectedPortfolio?.companyName || this.selectedPortfolioKey

        // Confirm import
        const confirmResult = await Swal.fire({
            title: 'Import Properties?',
            html: `
                <p>This will import <strong>${this.selectedPropertyIds.size}</strong> properties into:</p>
                <p><strong>${portfolioName}</strong></p>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, import',
            cancelButtonText: 'Cancel',
        })

        if (!confirmResult.isConfirmed) return

        this.isImporting = true
        this.lastImportResult = null

        try {
            const result = await this.redefineService.importProperties(
                this.selectedPortfolioKey,
                Array.from(this.selectedPropertyIds)
            )

            this.lastImportResult = result

            await Swal.fire({
                title: result.success ? 'Import Complete!' : 'Import Completed with Errors',
                html: `
                    <ul class="text-start">
                        <li><strong>${result.created}</strong> properties created</li>
                        <li><strong>${result.failed}</strong> failed</li>
                    </ul>
                `,
                icon: result.success ? 'success' : 'warning',
            })

            if (result.success) {
                this.selectedPropertyIds.clear()
            }
        } catch (error: any) {
            console.error('Error importing properties:', error)
            await Swal.fire({
                title: 'Error',
                text: error.message || 'Failed to import properties',
                icon: 'error',
            })
        } finally {
            this.isImporting = false
        }
    }

    /**
     * Sync GPS coordinates for all imported properties in the selected portfolio
     * This only updates coordinates, doesn't re-download images
     */
    async syncCoordinates(): Promise<void> {
        if (this.isSyncingCoordinates) return
        if (!this.selectedPortfolioKey) {
            await Swal.fire({
                title: 'Select Portfolio',
                text: 'Please select a portfolio to sync coordinates for',
                icon: 'warning',
            })
            return
        }

        // Find portfolio name
        const selectedPortfolio = this.portfolios.find((p) => p.$key === this.selectedPortfolioKey)
        const portfolioName = selectedPortfolio?.companyName || this.selectedPortfolioKey

        // Confirm sync
        const confirmResult = await Swal.fire({
            title: 'Sync GPS Coordinates?',
            html: `
                <p>This will update GPS coordinates for all imported Redefine properties in:</p>
                <p><strong>${portfolioName}</strong></p>
                <p class="text-muted small">This is much faster than re-importing as it doesn't download images.</p>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, sync',
            cancelButtonText: 'Cancel',
        })

        if (!confirmResult.isConfirmed) return

        this.isSyncingCoordinates = true

        try {
            // forceSync=true ensures all parkings are updated, including the root parkings collection
            // This is important to fix parkings that have coordinates but aren't showing on the map
            const result = await this.redefineService.syncCoordinates(this.selectedPortfolioKey, true)

            await Swal.fire({
                title: result.success ? 'Sync Complete!' : 'Sync Completed with Errors',
                html: `
                    <ul class="text-start">
                        <li><strong>${result.updated}</strong> parkings updated with coordinates</li>
                        <li><strong>${result.skipped}</strong> already had correct coordinates</li>
                        <li><strong>${result.failed}</strong> failed</li>
                    </ul>
                `,
                icon: result.success ? 'success' : 'warning',
            })
        } catch (error: any) {
            console.error('Error syncing coordinates:', error)
            await Swal.fire({
                title: 'Error',
                text: error.message || 'Failed to sync coordinates',
                icon: 'error',
            })
        } finally {
            this.isSyncingCoordinates = false
        }
    }

    /**
     * Get the first image URL for a property
     */
    getPropertyImage(property: RedefineProperty): string | null {
        // Use MainImageURL from list response
        if (property.MainImageURL) {
            return property.MainImageURL
        }
        // Fallback to Images array if available
        if (property.Images && property.Images.length > 0) {
            return property.Images[0].url
        }
        return null
    }

    /**
     * Get formatted address for a property
     */
    getPropertyAddress(property: RedefineProperty): string {
        const parts = [property.Address, property.Suburb, property.City, property.Province].filter(Boolean)
        return parts.join(', ') || 'No address'
    }
}
